import React, {
  ReactNode,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import FiltersIcon from 'core/icons/filters';
import Popover from 'core/components/Popover';
import Button from 'pages/Dashboard/components/Button';
import Badge from '@mui/material/Badge';
import {
  useFormContext,
} from 'react-hook-form';
import {
  isFilterEmpty,
} from 'core/Filters/util';
import isEmpty from 'lodash/isEmpty';

type Props = {
  clearAll?: () => void;
  applyFilter?: () => void;
  onClose?: () => void;
  label?: string;
  children: ReactNode;
  isLoading?: boolean;
  saveText?: string;
  closeText?: string;
};

const filterPanelChromeClasses = clsx(
  'w-80 !max-h-[80vh] !overflow-hidden flex flex-col justify-between',
  '!p-0 !shadow-2xl !shadow-gray-400 !rounded-lg !bg-white',
);

type FilterBadgeProps = {
  showBadge: boolean;
};

export function FilterBadge({ showBadge }: FilterBadgeProps) {
  return (
    <Badge
      color="secondary"
      variant="dot"
      invisible={!showBadge}
      sx={{
        '.MuiBadge-badge': {
          top: '-2px',
          right: '-2px',
        },
      }}
    >
      <FiltersIcon className="!w-4 !h-4 !text-neutral-700" />
    </Badge>
  );
}

export default function FilterWrapper({
  clearAll,
  applyFilter,
  label,
  onClose: onFilterClose,
  isLoading = false,
  children,
  saveText = 'Save and Apply',
  closeText = 'Close',
}: Props) {
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { formState: { errors }, watch } = useFormContext();

  const filterValues = watch();

  const hasError = !isEmpty(errors);
  const applied = useMemo(
    () => !isFilterEmpty(filterValues),
    [isOpened, filterValues],
  );

  const onClick = () => {
    setIsOpened(true);
  };

  const onClose = () => {
    setIsOpened(false);
    onFilterClose?.();
  };

  const onClear = () => {
    clearAll?.();
  };

  const handleSave = async () => {
    applyFilter?.();
    onClose();
  };

  return (
    <div>
      <Button
        ref={setAnchorEl}
        variant="outlined"
        className="nw-settings-filters !pl-2.5 flex gap-2"
        onClick={onClick}
      >
        <FilterBadge showBadge={applied} />
        <span className="hidden lg:block">{label}</span>
      </Button>
      <Popover
        anchorEl={isOpened ? anchorEl : null}
        onClose={onClose}
        anchorOrigin={{ vertical: -6, horizontal: -1 }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: filterPanelChromeClasses }}
        marginThreshold={null}
        disableAutoFocus
        disableEnforceFocus
        disableEscapeKeyDown={false}
      >
        <div className="flex justify-between items-center pl-3 pt-1.5">
          <h3 className="flex gap-2 items-center text-xs text-neutral-700 font-semibold">
            <FiltersIcon className="!w-4 !h-4 !text-neutral-700" />
            Filters
          </h3>
          <div className="flex gap-2 nw-clear-filters">
            <Button
              variant="text"
              color="primary"
              onClick={onClear}
            >
              Clear
            </Button>
            <IconButton
              onClick={onClose}
            >
              <CloseIcon className="!w-4 !h-4 !text-gray-400" />
            </IconButton>
          </div>
        </div>
        <div className="min-h-0 flex-1 w-full my-3 py-1 overflow-y-auto">
          <div className="w-full flex flex-col gap-4 px-4">
            {children}
          </div>
        </div>
        <div className="flex flex-col w-full gap-4 bg-white px-4 pb-4">
          <div className="flex flex-row justify-end gap-3">
            <Button
              variant="outlined"
              onClick={onClose}
              className="nw-close-settings"
              disabled={hasError}
            >
              {closeText}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className="nw-save-settings"
              onClick={handleSave}
              disabled={isLoading || hasError}
            >
              {isLoading ? 'Saving...' : saveText}
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
}
